/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListAIIntegrationSchedulesRequest,
  ListAIIntegrationSchedulesSecurity,
} from "../models/operations/listaiintegrationschedules.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AiIntegrationSchedulesQueryData,
  buildAiIntegrationSchedulesQuery,
  prefetchAiIntegrationSchedules,
  queryKeyAiIntegrationSchedules,
} from "./aiIntegrationSchedules.core.js";
export {
  type AiIntegrationSchedulesQueryData,
  buildAiIntegrationSchedulesQuery,
  prefetchAiIntegrationSchedules,
  queryKeyAiIntegrationSchedules,
};

export type AiIntegrationSchedulesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSchedules aiIntegrations
 *
 * @remarks
 * List the sync schedules and their scheduler state for a provider's org-wide AI integration config. Returns an empty list when no config is set.
 */
export function useAiIntegrationSchedules(
  request: ListAIIntegrationSchedulesRequest,
  security?: ListAIIntegrationSchedulesSecurity | undefined,
  options?: QueryHookOptions<
    AiIntegrationSchedulesQueryData,
    AiIntegrationSchedulesQueryError
  >,
): UseQueryResult<
  AiIntegrationSchedulesQueryData,
  AiIntegrationSchedulesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildAiIntegrationSchedulesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSchedules aiIntegrations
 *
 * @remarks
 * List the sync schedules and their scheduler state for a provider's org-wide AI integration config. Returns an empty list when no config is set.
 */
export function useAiIntegrationSchedulesSuspense(
  request: ListAIIntegrationSchedulesRequest,
  security?: ListAIIntegrationSchedulesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AiIntegrationSchedulesQueryData,
    AiIntegrationSchedulesQueryError
  >,
): UseSuspenseQueryResult<
  AiIntegrationSchedulesQueryData,
  AiIntegrationSchedulesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAiIntegrationSchedulesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAiIntegrationSchedulesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      provider: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: AiIntegrationSchedulesQueryData,
): AiIntegrationSchedulesQueryData | undefined {
  const key = queryKeyAiIntegrationSchedules(...queryKeyBase);

  return client.setQueryData<AiIntegrationSchedulesQueryData>(key, data);
}

export function invalidateAiIntegrationSchedules(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      provider: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "aiIntegrations",
      "listSchedules",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllAiIntegrationSchedules(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "aiIntegrations", "listSchedules"],
  });
}
